import { useEffect, useRef, useState } from "react";
import axiosInstance from "../../../api/axiosInstance";

const VARIANTS = {
  desktop: {
    title: "Web image",
    description: "Used on tablet and desktop screens.",
    outputWidth: 2400,
    outputHeight: 1350,
    frameWidth: 720,
    frameHeight: 405,
  },
  mobile: {
    title: "Mobile image",
    description: "Used on mobile screens.",
    outputWidth: 853,
    outputHeight: 1844,
    frameWidth: 320,
    frameHeight: 692,
  },
};

const buttonStyle = {
  border: 0,
  borderRadius: 7,
  background: "#2563eb",
  color: "#fff",
  padding: "8px 12px",
  fontWeight: 600,
  cursor: "pointer",
};

export default function HeroImageCropper() {
  const pickerRef = useRef(null);
  const dragRef = useRef(null);
  const [savedImages, setSavedImages] = useState({ desktop: "", mobile: "" });
  const [activeVariant, setActiveVariant] = useState("desktop");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [previewImage, setPreviewImage] = useState(null);
  const [sourceUrl, setSourceUrl] = useState("");
  const [sourceImage, setSourceImage] = useState(null);
  const [zoom, setZoom] = useState(1);
  const [cropX, setCropX] = useState(50);
  const [cropY, setCropY] = useState(50);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState("");
  const cropConfig = VARIANTS[activeVariant];

  useEffect(() => {
    axiosInstance.get("/api/superadmin/case-studies-page/")
      .then(({ data }) => {
        const page = (data?.data ?? data) || {};
        setSavedImages({
          desktop: page.hero_image_url || page.content?.hero?.image_url || "",
          mobile: page.content?.hero?.mobile_image_url || "",
        });
      })
      .catch(() => {});
  }, []);

  const openUploader = (variant) => {
    setActiveVariant(variant);
    setSourceUrl("");
    setSourceImage(null);
    setError("");
    setIsModalOpen(true);
  };

  const selectImage = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const nextUrl = URL.createObjectURL(file);
    const image = new Image();
    image.onload = () => {
      setSourceImage(image);
      setSourceUrl(nextUrl);
      setZoom(1);
      setCropX(50);
      setCropY(50);
      setError("");
    };
    image.src = nextUrl;
  };

  const baseScale = sourceImage ? Math.max(cropConfig.frameWidth / sourceImage.width, cropConfig.frameHeight / sourceImage.height) : 1;
  const previewWidth = sourceImage ? sourceImage.width * baseScale * zoom : cropConfig.frameWidth;
  const previewHeight = sourceImage ? sourceImage.height * baseScale * zoom : cropConfig.frameHeight;
  const excessX = Math.max(0, previewWidth - cropConfig.frameWidth);
  const excessY = Math.max(0, previewHeight - cropConfig.frameHeight);

  const imageStyle = {
    width: previewWidth,
    height: previewHeight,
    position: "absolute",
    left: -excessX * (cropX / 100),
    top: -excessY * (cropY / 100),
    maxWidth: "none",
    userSelect: "none",
    pointerEvents: "none",
  };

  const moveCrop = (event) => {
    if (!dragRef.current) return;
    if (excessX) setCropX(Math.max(0, Math.min(100, dragRef.current.x - ((event.clientX - dragRef.current.clientX) / excessX) * 100)));
    if (excessY) setCropY(Math.max(0, Math.min(100, dragRef.current.y - ((event.clientY - dragRef.current.clientY) / excessY) * 100)));
  };

  const uploadCroppedImage = async () => {
    if (!sourceImage) return;

    setIsUploading(true);
    setError("");
    try {
      const canvas = document.createElement("canvas");
      canvas.width = cropConfig.outputWidth;
      canvas.height = cropConfig.outputHeight;
      const context = canvas.getContext("2d");
      const aspectRatio = cropConfig.outputWidth / cropConfig.outputHeight;
      let cropWidth = sourceImage.width / zoom;
      let cropHeight = cropWidth / aspectRatio;

      if (cropHeight > sourceImage.height / zoom) {
        cropHeight = sourceImage.height / zoom;
        cropWidth = cropHeight * aspectRatio;
      }

      const sourceX = Math.max(0, Math.min(sourceImage.width - cropWidth, (sourceImage.width - cropWidth) * (cropX / 100)));
      const sourceY = Math.max(0, Math.min(sourceImage.height - cropHeight, (sourceImage.height - cropHeight) * (cropY / 100)));
      context.drawImage(sourceImage, sourceX, sourceY, cropWidth, cropHeight, 0, 0, cropConfig.outputWidth, cropConfig.outputHeight);

      const blob = await new Promise((resolve) => canvas.toBlob(resolve, "image/jpeg", 0.92));
      if (!blob) throw new Error("Unable to create the cropped image.");

      const formData = new FormData();
      formData.append("hero_image", blob, `case-studies-hero-${activeVariant}.jpg`);
      formData.append("image_variant", activeVariant);

      const { data } = await axiosInstance.post("/api/superadmin/case-studies-page/hero-image/", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const page = (data?.data ?? data) || {};
      const uploadedUrl = activeVariant === "desktop"
        ? page.hero_image_url
        : page.content?.hero?.mobile_image_url;

      setSavedImages((images) => ({ ...images, [activeVariant]: uploadedUrl || sourceUrl }));
      setSourceUrl("");
      setSourceImage(null);
      setIsModalOpen(false);
    } catch (uploadError) {
      setError(uploadError?.response?.data?.detail || "Could not upload the cropped hero image.");
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <div style={{ padding: 14, border: "1px dashed #93c5fd", borderRadius: 8, background: "#f8fbff", marginBottom: 16 }}>
      <div style={{ fontSize: 13, fontWeight: 700, color: "#1e40af", marginBottom: 4 }}>Hero background images</div>
      <p style={{ fontSize: 12, color: "#64748b", margin: "0 0 12px" }}>Upload separate optimized images for web and mobile. Both images are stored in S3.</p>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 12 }}>
        {Object.entries(VARIANTS).map(([variant, details]) => (
          <div key={variant} style={{ padding: 12, border: "1px solid #dbeafe", borderRadius: 8, background: "#fff" }}>
            <div style={{ display: "flex", justifyContent: "space-between", gap: 8, alignItems: "start" }}>
              <div>
                <div style={{ fontSize: 13, fontWeight: 700, color: "#1e293b" }}>{details.title}</div>
                <div style={{ fontSize: 12, color: "#64748b", marginTop: 2 }}>{details.description}</div>
              </div>
              <button type="button" onClick={() => openUploader(variant)} style={buttonStyle}>{savedImages[variant] ? "Change" : "Upload"}</button>
            </div>
            {savedImages[variant] ? (
              <img src={savedImages[variant]} alt={`Current ${details.title.toLowerCase()} hero`} onClick={() => setPreviewImage({ url: savedImages[variant], title: details.title })} style={{ marginTop: 10, display: "block", width: "100%", height: 450, objectFit: "cover", borderRadius: 6, border: "1px solid #dbeafe", cursor: "zoom-in" }} />
            ) : (
              <div style={{ marginTop: 10, height: 450, display: "grid", placeItems: "center", borderRadius: 6, background: "#f8fafc", color: "#94a3b8", fontSize: 12 }}>No image uploaded</div>
            )}
          </div>
        ))}
      </div>

      {isModalOpen && (
        <div onClick={() => !isUploading && setIsModalOpen(false)} style={{ position: "fixed", inset: 0, zIndex: 2000, display: "flex", alignItems: "center", justifyContent: "center", padding: 20, background: "rgba(15,23,42,.55)" }}>
          <div onClick={(event) => event.stopPropagation()} style={{ width: "min(800px, 100%)", maxHeight: "92vh", overflowY: "auto", background: "#fff", borderRadius: 14, boxShadow: "0 24px 64px rgba(0,0,0,.2)", padding: 22 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
              <div>
                <h2 style={{ margin: 0, fontSize: 18 }}>{VARIANTS[activeVariant].title} cropper</h2>
                <p style={{ margin: "4px 0 0", color: "#64748b", fontSize: 12 }}>Final output: {cropConfig.outputWidth} × {cropConfig.outputHeight}px.</p>
              </div>
              <button type="button" onClick={() => setIsModalOpen(false)} style={{ border: 0, background: "none", fontSize: 22, cursor: "pointer", color: "#64748b" }}>×</button>
            </div>

            {!sourceUrl ? (
              <input ref={pickerRef} type="file" accept="image/*" onChange={selectImage} style={{ fontSize: 13 }} />
            ) : (
              <>
                <div onPointerDown={(event) => { dragRef.current = { clientX: event.clientX, clientY: event.clientY, x: cropX, y: cropY }; event.currentTarget.setPointerCapture(event.pointerId); }} onPointerMove={moveCrop} onPointerUp={() => { dragRef.current = null; }} onPointerCancel={() => { dragRef.current = null; }} style={{ width: "100%", maxWidth: cropConfig.frameWidth, aspectRatio: `${cropConfig.outputWidth} / ${cropConfig.outputHeight}`, margin: "0 auto", overflow: "hidden", position: "relative", background: "#0f172a", cursor: "grab", touchAction: "none", borderRadius: 8 }}>
                  <img src={sourceUrl} alt="Crop preview" draggable="false" style={imageStyle} />
                </div>
                <p style={{ textAlign: "center", color: "#64748b", fontSize: 12, margin: "8px 0 14px" }}>Drag the image to fit it in the crop frame.</p>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: 12 }}>
                  <label style={{ fontSize: 12, color: "#475569" }}>Zoom<input type="range" min="1" max="3" step=".05" value={zoom} onChange={(event) => setZoom(Number(event.target.value))} style={{ width: "100%" }} /></label>
                  <label style={{ fontSize: 12, color: "#475569" }}>Horizontal crop<input type="range" min="0" max="100" value={cropX} onChange={(event) => setCropX(Number(event.target.value))} disabled={!excessX} style={{ width: "100%" }} /></label>
                  <label style={{ fontSize: 12, color: "#475569" }}>Vertical crop<input type="range" min="0" max="100" value={cropY} onChange={(event) => setCropY(Number(event.target.value))} disabled={!excessY} style={{ width: "100%" }} /></label>
                </div>
                <div style={{ display: "flex", justifyContent: "flex-end", gap: 9, marginTop: 18 }}>
                  <button type="button" onClick={() => { setSourceUrl(""); setSourceImage(null); }} style={{ padding: "8px 12px", border: "1px solid #cbd5e1", borderRadius: 7, background: "#fff", cursor: "pointer" }}>Choose another</button>
                  <button type="button" disabled={isUploading} onClick={uploadCroppedImage} style={{ ...buttonStyle, opacity: isUploading ? 0.7 : 1 }}>{isUploading ? "Uploading..." : "Crop & upload"}</button>
                </div>
              </>
            )}
            {error && <p style={{ color: "#dc2626", fontSize: 12 }}>{error}</p>}
          </div>
        </div>
      )}

      {previewImage && (
        <div onClick={() => setPreviewImage(null)} style={{ position: "fixed", inset: 0, zIndex: 2100, display: "flex", alignItems: "center", justifyContent: "center", padding: 24, background: "rgba(15,23,42,.8)", cursor: "zoom-out" }}>
          <div onClick={(event) => event.stopPropagation()} style={{ position: "relative", maxWidth: "min(1200px, 100%)", maxHeight: "100%" }}>
            <button type="button" onClick={() => setPreviewImage(null)} aria-label="Close full image preview" style={{ position: "absolute", top: 10, right: 10, zIndex: 1, width: 34, height: 34, border: 0, borderRadius: "50%", background: "rgba(15,23,42,.75)", color: "#fff", fontSize: 22, cursor: "pointer" }}>×</button>
            <img src={previewImage.url} alt={`Full ${previewImage.title.toLowerCase()} hero`} style={{ display: "block", maxWidth: "100%", maxHeight: "calc(100vh - 48px)", objectFit: "contain", borderRadius: 8 }} />
          </div>
        </div>
      )}
    </div>
  );
}
